"use client";

import { ArrowRightIcon } from "../lifestyleIcons";

interface CategoryCardProps {
  label: string;
  /** Omitted by the "All prizes" tile, which is outlined rather than illustrated. */
  image?: string;
  isSelected: boolean;
  /** True when the category has no prizes under the other active filters. */
  isDisabled?: boolean;
  onSelect: () => void;
}

/**
 * The arrow chip in the bottom-right corner of every tile.
 *
 * Absolute offsets resolve against the padding box, so the outlined tile insets
 * by 4px to sit the same 8px from the tile edge as the illustrated ones.
 */
function ArrowChip({ isOutlined = false }: { isOutlined?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute flex items-center justify-center size-8 bg-white rounded ${
        isOutlined ? "bottom-1 right-1 border border-black/8" : "bottom-2 right-2"
      }`}>
      <ArrowRightIcon className="size-6 text-primary" />
    </span>
  );
}

/**
 * One square tile in the Categories grid.
 *
 * The whole tile is the button — the arrow is decoration, so nesting a second
 * button inside it would be both invalid and a keyboard trap. Selection is a
 * 4px primary border on whichever tile matches the active category filter;
 * unselected tiles keep a transparent border so toggling selection does not
 * shift the grid.
 */
export default function CategoryCard({ label, image, isSelected, isDisabled = false, onSelect }: CategoryCardProps) {
  const isAllPrizes = !image;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isDisabled}
      aria-pressed={isSelected}
      className={`relative flex items-center justify-center w-full aspect-square overflow-hidden rounded-lg border-4 transition-opacity ${
        isSelected ? "border-primary" : isAllPrizes ? "border-black/8" : "border-transparent bg-gray-3"
      } ${isDisabled ? "opacity-38 cursor-not-allowed" : "hover:opacity-90"}`}>
      {isAllPrizes ? (
        <span className="text-sm font-medium uppercase text-primary">{label}</span>
      ) : (
        <>
          <img src={image} alt="" className="absolute inset-0 size-full object-cover" />
          <span className="absolute top-2 left-2 flex items-center min-h-5 px-1.5 text-sm font-medium text-dark-1 bg-white/24 rounded backdrop-blur-sm">
            {label}
          </span>
        </>
      )}

      <ArrowChip isOutlined={isAllPrizes} />
    </button>
  );
}
